import { LanguageService } from "libraries/service/language/language.service";
import { Component, OnDestroy, OnInit, Input, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { Yii } from "libraries/interfaces/Yii";
import { FormControl, FormGroup } from '@angular/forms';
import { IFieldSpecs } from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import { ISimpleSelectFieldConfig, ISimpleSelectOption } from 'libraries/module/formbuilder/fields/simple-select/simple-select.interface';
import { PhoneNumber, PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { debounceTime, distinctUntilChanged, pluck, tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { ILabels } from "libraries/interfaces/ILabels";
import { isPhoneNumber } from 'libraries/module/formbuilder/forms/utils/validators/isPhoneNumber.validator';
import { CountriesListService } from 'libraries/service/countries-list/countries-list.service';
import {
    IConditionField,
    IIqbhBookFormConfigEmergencyContactForm
} from "libraries/interfaces/i-iaqoboh-reservation-form-config";
import { LocalizedPipe } from "libraries/pipes/localized/localized.pipe";
import { IPhone } from "libraries/service/resources/hotel/IPostHotelBookingsCreate";
import { Required } from "libraries/module/formbuilder/forms/utils/validators/required.validator";

export interface IPhoneDetails extends IPhone {
    formattedNumber?: string;
    email?: string;
}

export interface IConfigurationField {
    type: string;
    label: string;
}

class Wording {
    public readonly title: string = Yii.t('wui-iqbh-js', "Contact d'urgence");
    public readonly prefix: string = Yii.t('wui-iqbh-js', "Indicatif");
}

@Component({
    selector: 'emergency-contact',
    template: `
        <card>
            <div class="card-header-content">
                <h1>{{ (configuration?.labelArray || wording.title) | localized}}</h1>
            </div>

            <div class="card-body-content card-marge-inside">
                <form [formGroup]="form">
                    <combobox-field *ngIf="emergencyContactPrefix?.isDisplayed"
                                    [fieldConfig]="emergencyContactPrefix"
                                    controlName="countryPrefix"
                                    [items]="emergencyContactPrefix.options"></combobox-field>

                    <text *ngIf="emergencyContactNumber?.isDisplayed"
                          [fieldConfig]="emergencyContactNumber"></text>
                    <text *ngIf="emergencyEmail?.isDisplayed"
                          [fieldConfig]="emergencyEmail"></text>
                </form>
            </div>
        </card>
    `,
    styles: [`
        text {
            display: block;
            margin-top: 15px;
        }
    `]
})
export class EmergencyContactComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input()
    public existingPhoneNumber: IPhone;

    @Input()
    public existingEmail: string;

    @Input()
    public configuration: IIqbhBookFormConfigEmergencyContactForm;

    @Input()
    public isPhoneRequired: boolean;

    public readonly wording: Wording = new Wording();

    public readonly form: FormGroup = new FormGroup({
        countryPrefix: new FormControl(''),
        areaPrefix: new FormControl(''),
        number: new FormControl(''),
        text: new FormControl(''),
        email: new FormControl(''),
    }, {
        validators: isPhoneNumber
    });

    public emergencyContactPrefix: ISimpleSelectFieldConfig;
    public emergencyContactNumber: IFieldSpecs;
    public emergencyEmail: IFieldSpecs;
    public emergencyPhone: IFieldSpecs;
    public subscriptions: Subscription = new Subscription();

    public isValidPhoneNumber: boolean = true;

    private readonly phoneNumberUtil: PhoneNumberUtil;
    private emergencyPhoneDetails: IPhoneDetails;

    constructor(
        private readonly countriesListService: CountriesListService,
        public readonly languageService: LanguageService,
        private readonly localizedPipe: LocalizedPipe,
    ) {
        this.phoneNumberUtil = PhoneNumberUtil.getInstance();
    }

    public ngOnInit(): void {

        const fieldArray: IConditionField[] = this.configuration.fieldArray;

        const configurationArray: IConfigurationField[] = [
            {type: 'number', label: Yii.t('wui-iqbh-js', 'Numéro')},
            {type: 'email', label: Yii.t('wui-iqbh-js', 'E-mail')},
            {type: 'portable', label: Yii.t('wui-iqbh-js', 'Téléphone portable')},
            {type: 'countryPrefix', label: Yii.t('wui-iqbh-js', 'Préfixe')},
        ];

        configurationArray.forEach((configurationItem: IConfigurationField): void => {
            const relatedFieldItem: IConditionField = fieldArray?.length ?
                fieldArray.find((conditionField: IConditionField): boolean => {
                    return conditionField.code === configurationItem.type && conditionField.isDisplayed;
                }) :
                null;

            if (relatedFieldItem) {
                const genericFieldSpecs: IFieldSpecs = {
                    isDisplayed: Boolean(relatedFieldItem.isDisplayed),
                    formControlName: configurationItem.type,
                    formGroup: this.form,
                    label: relatedFieldItem.labelArray?.length ?
                        this.localizedPipe.transform(relatedFieldItem.labelArray) :
                        configurationItem.label,
                };

                switch (configurationItem.type) {
                    case 'number':
                        if (this.isPhoneRequired) {
                            this.emergencyContactNumber = {
                                ...genericFieldSpecs,
                                withAsteriskIfRequired: true,
                                validators: [new Required()],
                            };
                        } else {
                            this.emergencyContactNumber = genericFieldSpecs;
                        }
                        break;
                    case 'email':
                        this.emergencyEmail = genericFieldSpecs;
                        break;
                    case 'phone':
                        this.emergencyPhone = genericFieldSpecs;
                        break;
                    case 'countryPrefix':
                        this.emergencyContactPrefix = {
                            ...genericFieldSpecs,
                            placeholder: relatedFieldItem.labelArray?.length ?
                                this.localizedPipe.transform(relatedFieldItem.labelArray) :
                                configurationItem.label,
                            options: Object.entries(this.countriesListService.getCountriesList(this.languageService.userLanguage))
                                .map(([value, label]: [string, string]): ISimpleSelectOption => ({value, label})),
                        };
                        break;
                    default:
                        break;
                }
            }
        });

        const valueConfigCountryPrefix: IConditionField = fieldArray.find((configFieldArray: IConditionField): string => {
            return configFieldArray.code === "countryPrefix" && configFieldArray.defaultValue;
        });

        if (valueConfigCountryPrefix) {
            this.emergencyContactPrefix.formGroup.get('countryPrefix').patchValue(valueConfigCountryPrefix.defaultValue);
        } else {

            const defaultPrefixFromLanguage: {[key: string]: string} = {
                fr: 'FR',
                en: 'GB',
                pt: 'PT',
                it: 'IT',
                es: 'ES',
            }

            this.emergencyContactPrefix.formGroup.get('countryPrefix').patchValue(defaultPrefixFromLanguage[this.languageService.userLanguage]);
        }

        const formValueChanges: Subscription = this.form.valueChanges.pipe(
            // debounceTime(300),
            // distinctUntilChanged(),
            tap((values: IPhone): void => {
                if (values.number) {
                    const phoneData: IPhoneDetails = this.formatNumberByType(
                        values.number,
                        values.countryPrefix || this.languageService.userLanguage.toUpperCase()
                    );
                    this.emergencyPhoneDetails = phoneData;

                    if (phoneData) {
                        this.form.get('number').patchValue(phoneData.formattedNumber, {emitEvent: false, onlySelf: true});
                    }
                } else {
                    this.isValidPhoneNumber = true;
                    this.emergencyPhoneDetails = null;
                }
                if (values.email) {
                    this.emergencyPhoneDetails = {
                        ...(this.emergencyPhoneDetails || {}),
                        email: values.email,
                    };
                }
            })
        ).subscribe();

        const countryPrefixValueChanges: Subscription = this.form.get('countryPrefix').valueChanges.pipe(
            tap((): void => {
                this.form.get('number').patchValue('');
            })
        ).subscribe();

        this.subscriptions
            .add(formValueChanges)
            .add(countryPrefixValueChanges);

    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.configuration?.currentValue) {
            const configuration: IIqbhBookFormConfigEmergencyContactForm = changes.configuration.currentValue;
        }
    }

    public ngAfterViewInit(): void {
        if (this.existingPhoneNumber) {
            const phoneNumber: PhoneNumber = this.phoneNumberUtil.parseAndKeepRawInput(this.existingPhoneNumber.text);
            this.emergencyContactPrefix.formGroup.get('countryPrefix').patchValue(this.phoneNumberUtil.getRegionCodeForNumber(phoneNumber));
            this.emergencyContactNumber.formGroup.get('number').patchValue(this.existingPhoneNumber.text);

        }
        if (this.existingEmail) {
            this.emergencyEmail.formGroup.get('email').patchValue(this.existingEmail);
        }
    }

    public formatNumberByType(number: string, countryCode: string): IPhoneDetails {
        const country: string = countryCode.toUpperCase();
        if (this.phoneNumberUtil.isPossibleNumberString(number, country)) {
            const phoneNumber: PhoneNumber = this.phoneNumberUtil.parseAndKeepRawInput(number, country);
            const emergencyPhone: string = this.phoneNumberUtil.format(phoneNumber, PhoneNumberFormat.NATIONAL);
            const areaCodeLength: number = this.phoneNumberUtil.getLengthOfGeographicalAreaCode(phoneNumber);

            let areaPrefix: string = null;

            if (areaCodeLength > 0) {
                areaPrefix = `${phoneNumber.getNationalNumber()}`.substring(0, areaCodeLength);
            }

            this.isValidPhoneNumber = true;

            return {
                areaPrefix,
                number: `${phoneNumber.getNationalNumber()}`,
                countryPrefix: `${phoneNumber.getCountryCode()}`,
                text: `${this.phoneNumberUtil.format(phoneNumber, PhoneNumberFormat.E164)}`,
                formattedNumber: emergencyPhone
            };

        }else{
            this.isValidPhoneNumber = false;
        }
    }

    public ngOnDestroy(): void {
        if (this.subscriptions) {
            this.subscriptions.unsubscribe();
        }
    }

    public extractPhoneNumber(): IPhone {
        if (this.emergencyPhoneDetails) {
            const emergencyPhoneDetails: IPhoneDetails = Object.entries(this.emergencyPhoneDetails)
                .reduce((previous: IPhoneDetails, [key, value]: [string, string]): IPhoneDetails => {
                    return value ? {
                        ...previous,
                        [key]: value
                    } : previous;
                }, {});
            if (emergencyPhoneDetails.number) {
                emergencyPhoneDetails.number = emergencyPhoneDetails.number.replace(emergencyPhoneDetails.areaPrefix, '');
            }
            const {formattedNumber, ...phoneDetails}: IPhoneDetails = emergencyPhoneDetails;

            return phoneDetails;
        }

        return undefined;
    }
}
